"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FieldState = void 0;
var mobx_1 = require("mobx");
var types_1 = require("./types");
var utils_1 = require("../internal/utils");
/**
 * Helps maintain the value + error information about a field
 *
 * This is the glue between the *page* and *field* in the presence of invalid states.
 */
var FieldState = /** @class */ (function () {
    function FieldState(_initValue) {
        var _this = this;
        Object.defineProperty(this, "_initValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: _initValue
        });
        /**
         * The value you should bind to the input in your field.
         */
        Object.defineProperty(this, "value", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** If there is any error on the field on last validation attempt */
        Object.defineProperty(this, "error", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** If the field has been touched */
        Object.defineProperty(this, "dirty", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** The value set from code or a `value` that's been validated */
        Object.defineProperty(this, "$", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Set to true if a validation run has been completed since init
         * Use case:
         * - to show a green color in the field : `hasError == false && hasBeenValidated == true`
         **/
        Object.defineProperty(this, "hasBeenValidated", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /**
         * Allows you to preserve the `_autoValidationEnabled` value across `reinit`s
         */
        Object.defineProperty(this, "_autoValidationDefault", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "setAutoValidationDefault", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: function (autoValidationDefault) {
                _this._autoValidationDefault = autoValidationDefault;
                _this._autoValidationEnabled = autoValidationDefault;
                return _this;
            }
        });
        Object.defineProperty(this, "_autoValidationEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: this._autoValidationDefault
        });
        Object.defineProperty(this, "_validators", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "validators", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: function () {
                var validators = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    validators[_i] = arguments[_i];
                }
                _this._validators = validators;
                return _this;
            }
        });
        Object.defineProperty(this, "_onUpdate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Allows you to take actions in your code based on `value` changes caused by user interactions
         */
        Object.defineProperty(this, "_onDidChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Trackers for validation */
        Object.defineProperty(this, "lastValidationRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "preventNextQueuedValidation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "validating", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /**
         * Runs validation with debouncing to keep the UI super smoothly responsive
         * NOTE:
         * - also setup in constructor
         * - Not using `action` from mobx *here* as it throws off our type definitions
         */
        Object.defineProperty(this, "queueValidation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: utils_1.debounce(this.queuedValidationWakeup, 200)
        });
        /**
         * Composible fields (fields that work in conjuction with FormState)
         */
        Object.defineProperty(this, "_on$ValidationPass", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: function () { }
        });
        Object.defineProperty(this, "_on$Reinit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: function () { }
        });
        Object.defineProperty(this, "_setCompositionParent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: function (config) {
                _this._on$ValidationPass = function () { return mobx_1.runInAction(config.on$ValidationPass); };
                _this._on$Reinit = function () { return mobx_1.runInAction(config.on$Reinit); };
            }
        });
        mobx_1.makeObservable(this, {
            value: mobx_1.observable,
            error: mobx_1.observable,
            setError: mobx_1.action,
            dirty: mobx_1.observable,
            $: mobx_1.observable,
            hasBeenValidated: mobx_1.observable,
            _autoValidationDefault: mobx_1.observable,
            setAutoValidationDefault: mobx_1.action,
            getAutoValidationDefault: mobx_1.action.bound,
            _autoValidationEnabled: mobx_1.observable,
            enableAutoValidation: mobx_1.action.bound,
            enableAutoValidationAndValidate: mobx_1.action.bound,
            disableAutoValidation: mobx_1.action.bound,
            validators: mobx_1.action,
            onUpdate: mobx_1.action.bound,
            executeOnUpdate: mobx_1.action.bound,
            onDidChange: mobx_1.action.bound,
            executeOnDidChange: mobx_1.action.bound,
            setAutoValidationDebouncedMs: mobx_1.action.bound,
            lastValidationRequest: mobx_1.observable,
            preventNextQueuedValidation: mobx_1.observable,
            onChange: mobx_1.action.bound,
            reset: mobx_1.action.bound,
            validating: mobx_1.observable,
            validate: mobx_1.action.bound,
            queuedValidationWakeup: mobx_1.action.bound,
            _setCompositionParent: mobx_1.action
        });
        mobx_1.runInAction(function () {
            _this.value = _initValue;
            _this.$ = _initValue;
            /**
             * Automatic validation configuration
             */
            _this.queueValidation = mobx_1.action(utils_1.debounce(_this.queuedValidationWakeup, 200));
            _this._autoValidationEnabled = true;
        });
    }
    /**
     * Allows you to set an error on a field lazily
     * Use case:
     *  You validate some things on client (e.g. isRequired)
     *  You then validate the field on the backend with an explict action (e.g. continue button)
     *  You now want to highlight an error from the backend for this field
     **/
    Object.defineProperty(FieldState.prototype, "setError", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (error) {
            this.error = error;
        }
    });
    Object.defineProperty(FieldState.prototype, "getAutoValidationDefault", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            return this._autoValidationDefault;
        }
    });
    Object.defineProperty(FieldState.prototype, "enableAutoValidation", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            this._autoValidationEnabled = true;
            return this;
        }
    });
    Object.defineProperty(FieldState.prototype, "enableAutoValidationAndValidate", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            this._autoValidationEnabled = true;
            return this.validate();
        }
    });
    Object.defineProperty(FieldState.prototype, "disableAutoValidation", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            this._autoValidationEnabled = false;
            return this;
        }
    });
    /**
     * onUpdate is called whenever we change something in our local state that is significant
     * - any validation() call
     * - any reset() call
     */
    Object.defineProperty(FieldState.prototype, "onUpdate", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (handler) {
            this._onUpdate = handler;
            return this;
        }
    });
    Object.defineProperty(FieldState.prototype, "executeOnUpdate", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            this._onUpdate && this._onUpdate(this);
        }
    });
    Object.defineProperty(FieldState.prototype, "onDidChange", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (handler) {
            this._onDidChange = handler;
            return this;
        }
    });
    Object.defineProperty(FieldState.prototype, "executeOnDidChange", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (config) {
            this._onDidChange && this._onDidChange(config);
        }
    });
    Object.defineProperty(FieldState.prototype, "setAutoValidationDebouncedMs", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (milliseconds) {
            this.queueValidation = mobx_1.action(utils_1.debounce(this.queuedValidationWakeup, milliseconds));
            return this;
        }
    });
    /** On change on the component side */
    Object.defineProperty(FieldState.prototype, "onChange", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (value) {
            // no long prevent any debounced validation request
            this.preventNextQueuedValidation = false;
            // Store local old value for onDidChange
            var oldValue = this.value;
            // Immediately set for local ui binding
            this.value = value;
            // Call on did change if any
            this.executeOnDidChange({ newValue: value, oldValue: oldValue });
            this.dirty = true;
            this.executeOnUpdate();
            if (this._autoValidationEnabled) {
                this.queueValidation();
            }
        }
    });
    /**
     * If the page wants to reinitialize the field,
     * it should call this function
     */
    Object.defineProperty(FieldState.prototype, "reset", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (value) {
            if (value === void 0) { value = this._initValue; }
            // If a previous validation comes back ignore it
            this.preventNextQueuedValidation = true;
            // This value vetos all previous values
            this._autoValidationEnabled = this._autoValidationDefault;
            this.value = value;
            this.error = undefined;
            this.dirty = false;
            this.hasBeenValidated = false;
            this.$ = value;
            this._on$Reinit();
            this.executeOnUpdate();
        }
    });
    Object.defineProperty(FieldState.prototype, "hasError", {
        get: function () {
            return !!this.error;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Runs validation on the current value immediately
     */
    Object.defineProperty(FieldState.prototype, "validate", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            var _this = this;
            this.lastValidationRequest++;
            var lastValidationRequest = this.lastValidationRequest;
            this.validating = true;
            var value = this.value;
            return types_1.applyValidators(this.value, this._validators || [])
                .then(mobx_1.action(function (fieldError) {
                /**
                 * If validation comes back out of order then the result of this validation is not siginificant
                 * We simply copy the value from the last validation attempt
                 */
                if (_this.lastValidationRequest !== lastValidationRequest) {
                    if (_this.hasError) {
                        return { hasError: true };
                    }
                    else {
                        return {
                            hasError: false,
                            value: _this.$,
                        };
                    }
                }
                _this.validating = false;
                _this.hasBeenValidated = true;
                /** For any change in field error, update our error */
                if (fieldError != _this.error) {
                    _this.error = fieldError;
                }
                /** Check for error */
                var hasError = _this.hasError;
                /** If no error */
                if (!hasError) {
                    /** Copy over the value to validated value */
                    if (_this.$ !== value) {
                        _this.$ = value;
                    }
                    /** Trigger any form level validations if required */
                    _this._on$ValidationPass();
                }
                /** before returning update */
                _this.executeOnUpdate();
                /** return a result based on error status */
                if (hasError) {
                    return { hasError: true };
                }
                else {
                    return {
                        hasError: false,
                        value: value
                    };
                }
            }));
        }
    });
    Object.defineProperty(FieldState.prototype, "queuedValidationWakeup", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            if (this.preventNextQueuedValidation) {
                this.preventNextQueuedValidation = false;
                return;
            }
            this.validate();
        }
    });
    return FieldState;
}());
exports.FieldState = FieldState;
